import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Receipt, AlertCircle, Users, Anchor, Ship, Clock, CheckCircle, PieChart, Calendar, DollarSign, Cloud, Wind, Flame, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import ExchangeRatesWidget from '../components/ExchangeRatesWidget';
import { PortCallChart, InvoiceStatsChart } from '../components/dashboard';

// Gemi Kaza API
const CASUALTY_API_URL = 'https://eqnwtqdfhmtayduypxxf.supabase.co/functions/v1/client-api';
const CASUALTY_API_KEY = '75100747-b913-4800-8e38-6ab3b6fe6b77';

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [anchorageStats, setAnchorageStats] = useState(null);
  const [motorLaunchStats, setMotorLaunchStats] = useState(null);
  const [portCallStats, setPortCallStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportingLoading, setReportingLoading] = useState(true);
  const [motorPeriod, setMotorPeriod] = useState(1);
  const [windyKey, setWindyKey] = useState(Date.now());
  const windyContainerRef = useRef(null);
  
  // Gemi Kaza Verileri
  const [casualties, setCasualties] = useState([]);
  const [casualtiesLoading, setCasualtiesLoading] = useState(true);

  // İngilizce -> Türkçe çeviri sözlüğü (gemi kazaları için)
  const translateToTurkish = (text) => {
    if (!text) return '';
    
    const translations = {
      // Cümle kalıpları (önce bunlar işlenir)
      'ran aground': 'karaya oturdu',
      'caught fire': 'yangın çıktı',
      'lost power': 'güç kaybetti',
      'Coast Guard': 'Sahil Güvenlik',
      'engine room': 'makine dairesi',
      'crew members': 'mürettebat üyeleri',
      'aircraft carrier': 'uçak gemisi',
      'crude oil': 'ham petrol',
      'fuel leak': 'yakıt sızıntısı',
      'oil spill': 'petrol sızıntısı',
      'severe weather': 'şiddetli hava',
      'product tanker': 'ürün tankeri',
      'oil tanker': 'petrol tankeri',
      'cargo ship': 'kargo gemisi',
      'container ship': 'konteyner gemisi',
      'ferry quay': 'feribot iskelesi',
      'successfully rescued': 'başarıyla kurtarıldı',
      'no casualties': 'can kaybı yok',
      'no pollution': 'kirlilik yok',
      'Maritime Board': 'Denizcilik Kurulu',
      'Coastal Safety': 'Kıyı Emniyeti',
      'bunkering operations': 'yakıt ikmali operasyonları',
      'Mediterranean Sea': 'Akdeniz',
      'Black Sea': 'Karadeniz',
      'Arabian Gulf': 'Basra Körfezi',
      'at Italian port': 'İtalyan limanında',
      'in Norway': 'Norveç\'te',
      'in Canada': 'Kanada\'da',
      'in Turkey': 'Türkiye\'de',
      'off Turkey': 'Türkiye açıklarında',
      'preparing to depart': 'hareket etmeye hazırlanırken',
      'starboard bow': 'sancak baş',
      'port side': 'iskele tarafı',
      'with no': 'olmadan',
      'due to': 'nedeniyle',
      // Fiiller
      'rescued': 'kurtarıldı',
      'stranded': 'mahsur kaldı',
      'collided': 'çarpıştı',
      'reported': 'bildirdi',
      'occurred': 'meydana geldi',
      'towed': 'çekildi',
      'inspected': 'incelendi',
      'refloated': 'yüzdürüldü',
      'experienced': 'yaşadı',
      'described': 'tanımlandı',
      'provided': 'sağladı',
      'causing': 'neden olarak',
      'involving': 'içeren',
      'sinking': 'batan',
      'losing': 'kaybederek',
      // İsimler
      'ship': 'gemi',
      'vessel': 'gemi',
      'tanker': 'tanker',
      'cargo': 'kargo',
      'crew': 'mürettebat',
      'captain': 'kaptan',
      'engine': 'motor',
      'fire': 'yangın',
      'flames': 'alevler',
      'smoke': 'duman',
      'explosion': 'patlama',
      'explosions': 'patlamalar',
      'blast': 'patlama',
      'blasts': 'patlamalar',
      'collision': 'çarpışma',
      'damage': 'hasar',
      'port': 'liman',
      'harbor': 'liman',
      'sea': 'deniz',
      'coast': 'kıyı',
      'coastal': 'kıyısal',
      'offshore': 'açık deniz',
      'anchor': 'demir',
      'anchorage': 'demirleme',
      'fuel': 'yakıt',
      'oil': 'petrol',
      'leak': 'sızıntı',
      'pump': 'pompa',
      'weather': 'hava',
      'conditions': 'koşulları',
      'storm': 'fırtına',
      'investigation': 'soruşturma',
      'authorities': 'yetkililer',
      'safety': 'güvenlik',
      'security': 'güvenlik',
      'incident': 'olay',
      'replacement': 'yedek',
      'operations': 'operasyonlar',
      'assistance': 'yardım',
      // Sıfatlar
      'Iranian': 'İran',
      'Turkish': 'Türk',
      'Italian': 'İtalyan',
      'Norwegian': 'Norveç',
      'Canadian': 'Kanada',
      'malfunctioning': 'arızalı',
      'severe': 'şiddetli',
      'safely': 'güvenli şekilde',
      'successfully': 'başarıyla',
      // Bağlaçlar ve edatlar
      'from': '-den',
      'near': 'yakınında',
      'off': 'açıklarında',
      'after': 'sonra',
      'during': 'sırasında',
      'with': 'ile',
      'and': 've',
      'for': 'için',
      'to': '-e',
      'in': '-de',
      'at': '-da',
      'by': 'tarafından',
      'as': 'olarak',
      'what': 'ne',
      'or': 'veya',
      // Artikeller (kaldırılacak)
      'The': '',
      'the': '',
      'A': 'Bir',
      'a': 'bir',
      'An': 'Bir',
      'an': 'bir',
      // Yardımcı fiiller (kaldırılacak)
      'was': '',
      'were': '',
      'is': '',
      'are': '',
      'has': '',
      'have': '',
      'been': '',
      'being': '',
      'It': '',
      'it': '',
      // Sayılar
      'seven': '7',
      'six': '6',
      'five': '5',
      'four': '4',
      'three': '3',
      'two': '2',
      'one': '1',
    };
    
    let result = text;
    
    // Önce uzun ifadeleri çevir (uzunluğa göre sırala)
    const sortedKeys = Object.keys(translations).sort((a, b) => b.length - a.length);
    for (const eng of sortedKeys) {
      const tr = translations[eng];
      const regex = new RegExp(`\\b${eng}\\b`, 'gi');
      result = result.replace(regex, tr);
    }
    
    // Gereksiz boşlukları ve noktalama temizle
    result = result.replace(/\s+/g, ' ').replace(/\s+\./g, '.').replace(/\s+,/g, ',').trim();
    
    return result;
  };

  // Gemi Kaza Verilerini Yükle
  const loadCasualties = async () => {
    setCasualtiesLoading(true);
    try {
      const response = await fetch(
        `${CASUALTY_API_URL}?resource=trial_casualties&order=CasualtyDate.desc`,
        {
          headers: {
            'API-key': CASUALTY_API_KEY
          }
        }
      );
      const result = await response.json();
      if (result.status === 200 && result.data) {
        setCasualties(result.data.slice(0, 10)); // Son 10 kaza
      }
    } catch (error) {
      console.error('Kaza verileri yüklenemedi:', error);
    } finally {
      setCasualtiesLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    loadReportingData();
    loadCasualties();
    
    // Windy iframe'i sayfa açıldığında yenile
    setWindyKey(Date.now());

    // Otomatik yenileme: Her 30 saniyede bir
    const intervalId = setInterval(() => {
      loadDashboard();
    }, 30000);

    // Sayfa odaklandığında yenile
    const handleFocus = () => {
      loadDashboard();
      loadCasualties();
      setWindyKey(Date.now()); // Windy'yi de yenile
    };
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadDashboard = async (motorMonths = motorPeriod) => {
    try {
      const [dashboardRes, anchorageRes, motorRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/anchorage/stats/summary'),
        api.get(`/motor-launches/stats/summary?months=${motorMonths}`)
      ]);
      setStats(dashboardRes.data);
      setAnchorageStats(anchorageRes.data);
      setMotorLaunchStats(motorRes.data);
    } catch (error) {
      // İlk yüklemede hata göster, otomatik yenilemelerde sessiz başarısız ol
      if (loading) {
        toast.error('Kontrol paneli yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  // Motor period değiştiğinde verileri yeniden yükle
  const handleMotorPeriodChange = (months) => {
    setMotorPeriod(months);
    loadDashboard(months);
  };

  const loadReportingData = async () => {
    setReportingLoading(true);
    try {
      const [portCallRes, invoiceRes] = await Promise.all([
        api.get('/dashboard/port-call-stats'),
        api.get('/dashboard/invoice-stats')
      ]);
      setPortCallStats(portCallRes.data);
      setInvoiceStats(invoiceRes.data);
    } catch (error) {
      console.error('Raporlama verileri yüklenemedi:', error);
    } finally {
      setReportingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="topbar">
          <h1 className="topbar-title" data-testid="page-title">{t('dashboard.title')}</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  // Modül bölüm stili
  const sectionStyle = {
    marginBottom: '2rem',
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.25rem',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #e9ecef'
  };

  const sectionTitleStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1e3a5f',
    margin: 0
  };

  const sectionIconStyle = (color) => ({
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: color,
    color: '#fff'
  });

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem'
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">{t('dashboard.title')}</h1>
        <div className="topbar-actions">
          <span className="text-muted" data-testid="dashboard-date">
            {new Date().toLocaleDateString('tr-TR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>
      
      <div style={{ padding: '2rem' }}>
        
        {/* Hava Durumu - Windy Widget */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionIconStyle('#0277bd')}>
              <Wind size={20} />
            </div>
            <h2 style={sectionTitleStyle}>Hava Durumu & Rüzgar Tahmini</h2>
            <div style={{ marginLeft: 'auto' }}>
              <button 
                onClick={() => setWindyKey(Date.now())}
                className="btn btn-secondary"
                style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
              >
                <Cloud size={14} style={{ marginRight: '0.25rem' }} />
                Yenile
              </button>
            </div>
          </div>
          <div 
            ref={windyContainerRef}
            className="windy-container"
            style={{ 
              width: '100%', 
              borderRadius: '8px', 
              overflow: 'hidden',
              background: '#f8fafc'
            }}
          >
            <iframe
              key={windyKey}
              title="Windy Hava Durumu"
              src="https://embed.windy.com/embed2.html?lat=42.006&lon=35.105&detailLat=42.006&detailLon=35.105&width=650&height=450&zoom=9&level=surface&overlay=wind&product=ecmwf&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=true&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1"
              frameBorder="0"
              style={{ 
                width: '100%', 
                height: '450px',
                border: 'none',
                borderRadius: '8px'
              }}
              allowFullScreen
            />
          </div>
          <div style={{ 
            marginTop: '0.75rem', 
            fontSize: '0.8rem', 
            color: '#64748b',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <span>📍 Samsun Limanı Bölgesi (42.006°N, 35.105°E)</span>
            <span>Kaynak: <a href="https://www.windy.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0277bd' }}>Windy.com</a></span>
          </div>
        </div>

        {/* Gemi Kazaları - Ship Casualties */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionIconStyle('#dc3545')}>
              <AlertTriangle size={20} />
            </div>
            <h2 style={sectionTitleStyle}>Dünya Gemi Kazaları</h2>
            <div style={{ marginLeft: 'auto' }}>
              <button 
                onClick={loadCasualties}
                className="btn btn-secondary"
                style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
              >
                <AlertTriangle size={14} style={{ marginRight: '0.25rem' }} />
                Yenile
              </button>
            </div>
          </div>
          
          {casualtiesLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              Kaza verileri yükleniyor...
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Tarih</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Gemi Adı</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>IMO</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Kaza Tipi</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {casualties.map((c, idx) => {
                    // Kaza tipi çevirileri ve renkleri
                    const typeTranslations = {
                      'Fire': { tr: 'Yangın', bg: '#fee2e2', text: '#dc2626', icon: '🔥' },
                      'Sank': { tr: 'Battı', bg: '#dbeafe', text: '#2563eb', icon: '🌊' },
                      'Collision': { tr: 'Çarpışma', bg: '#fef3c7', text: '#d97706', icon: '💥' },
                      'Beached/Grounded': { tr: 'Karaya Oturdu', bg: '#d1fae5', text: '#059669', icon: '⚓' },
                      'Engine Fault': { tr: 'Motor Arızası', bg: '#f3e8ff', text: '#7c3aed', icon: '⚙️' },
                      'Mechanical Fault': { tr: 'Mekanik Arıza', bg: '#f3e8ff', text: '#7c3aed', icon: '🔧' },
                      'Capsized': { tr: 'Alabora', bg: '#fecaca', text: '#b91c1c', icon: '🔄' },
                      'Explosion': { tr: 'Patlama', bg: '#fee2e2', text: '#dc2626', icon: '💥' },
                      'Flooding': { tr: 'Su Alma', bg: '#dbeafe', text: '#2563eb', icon: '💧' },
                      'Missing': { tr: 'Kayıp', bg: '#fef3c7', text: '#d97706', icon: '❓' },
                      'Piracy': { tr: 'Korsanlık', bg: '#fecaca', text: '#b91c1c', icon: '🏴‍☠️' },
                      'Other': { tr: 'Diğer', bg: '#f1f5f9', text: '#475569', icon: '⚠️' }
                    };
                    const typeInfo = typeTranslations[c.CasualtyType] || typeTranslations['Other'];
                    
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                          {new Date(c.CasualtyDate).toLocaleDateString('tr-TR')}
                        </td>
                        <td style={{ padding: '0.75rem', fontWeight: 600 }}>
                          {c.VesselName}
                        </td>
                        <td style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.8rem' }}>
                          {c.IMONumber}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            background: typeInfo.bg,
                            color: typeInfo.text
                          }}>
                            {typeInfo.icon} {typeInfo.tr}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#475569', maxWidth: '300px' }}>
                          {(() => {
                            const translated = translateToTurkish(c.CasualtyDetails || '');
                            return translated.length > 120 
                              ? translated.substring(0, 120) + '...' 
                              : translated;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ 
            marginTop: '0.75rem', 
            fontSize: '0.75rem', 
            color: '#94a3b8',
            textAlign: 'right'
          }}>
            Son güncelleme: {new Date().toLocaleString('tr-TR')}
          </div>
        </div>

        {/* Finans & Muhasebe Modülü */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionIconStyle('#28a745')}>
              <DollarSign size={20} />
            </div>
            <h2 style={sectionTitleStyle}>Finans & Muhasebe</h2>
          </div>
          <div style={statsGridStyle}>
            <div className="stat-card" data-testid="stat-proformas">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">{	('dashboard.activeProformas')}</div>
                  <div className="stat-card-value">{stats?.active_proformas || 0}</div>
                </div>
                <div className="stat-card-icon blue">
                  <FileText size={24} />
                </div>
              </div>
            </div>
            
            <div className="stat-card" data-testid="stat-invoices">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">{	('dashboard.pendingInvoices')}</div>
                  <div className="stat-card-value">{stats?.pending_invoices || 0}</div>
                </div>
                <div className="stat-card-icon green">
                  <Receipt size={24} />
                </div>
              </div>
            </div>
            
            <div className="stat-card" data-testid="stat-overdue">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">{	('dashboard.overdueInvoices')}</div>
                  <div className="stat-card-value" style={{ color: (stats?.overdue_invoices || 0) > 0 ? '#dc3545' : 'inherit' }}>
                    {stats?.overdue_invoices || 0}
                  </div>
                </div>
                <div className="stat-card-icon red">
                  <AlertCircle size={24} />
                </div>
              </div>
            </div>
            
            <div className="stat-card" data-testid="stat-clients">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">Toplam Müşteri</div>
                  <div className="stat-card-value">{stats?.total_clients || 0}</div>
                </div>
                <div className="stat-card-icon blue">
                  <Users size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Port Call ve Fatura İstatistikleri */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Port Call Dağılımı */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div style={sectionIconStyle('#1e3a5f')}>
                <PieChart size={20} />
              </div>
              <h2 style={sectionTitleStyle}>Liman Çağrısı Dağılımı</h2>
            </div>
            <PortCallChart data={portCallStats} loading={reportingLoading} />
          </div>
        </div>

        {/* Demirsahası Takip Modülü */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionIconStyle('#1e3a5f')}>
              <Anchor size={20} />
            </div>
            <h2 style={sectionTitleStyle}>Demirsahası Takip</h2>
            <a href="/anchorage" style={{ marginLeft: 'auto', fontSize: '0.9rem', color: '#1e88e5', textDecoration: 'none' }}>
              Detaylı Görüntüle →
            </a>
          </div>
          <div style={statsGridStyle}>
            <div className="stat-card" data-testid="stat-anchorage-total">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">Demirsahasındaki Gemiler</div>
                  <div className="stat-card-value">{anchorageStats?.total_anchored || 0}</div>
                </div>
                <div className="stat-card-icon blue">
                  <Anchor size={24} />
                </div>
              </div>
              {(anchorageStats?.total_anchored || 0) > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85em', color: '#666' }}>
                  <CheckCircle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  {anchorageStats?.normal || 0} normal durumda
                </div>
              )}
            </div>

            <div className="stat-card" data-testid="stat-anchorage-warning">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">Kritik Durum</div>
                  <div className="stat-card-value" style={{ color: (anchorageStats?.critical_warning || 0) > 0 ? '#ffc107' : 'inherit' }}>
                    {anchorageStats?.critical_warning || 0}
                  </div>
                </div>
                <div className="stat-card-icon orange">
                  <Clock size={24} />
                </div>
              </div>
              {(anchorageStats?.critical_warning || 0) > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85em', color: '#ffc107' }}>
                  156-168 saat arası
                </div>
              )}
            </div>

            <div className="stat-card" data-testid="stat-anchorage-critical">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">Süre Aşımı</div>
                  <div className="stat-card-value" style={{ color: (anchorageStats?.exceeded_limit || 0) > 0 ? '#dc3545' : 'inherit' }}>
                    {anchorageStats?.exceeded_limit || 0}
                  </div>
                </div>
                <div className="stat-card-icon red">
                  <AlertCircle size={24} />
                </div>
              </div>
              {(anchorageStats?.exceeded_limit || 0) > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85em', color: '#dc3545' }}>
                  168 saat sınırı aşıldı!
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Motor Seferleri Modülü */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionIconStyle('#0288d1')}>
              <Ship size={20} />
            </div>
            <h2 style={sectionTitleStyle}>Motor Seferleri</h2>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {[
                  { label: '1 Ay', value: 1 },
                  { label: '3 Ay', value: 3 },
                  { label: '6 Ay', value: 6 },
                  { label: 'Yıllık', value: 12 }
                ].map(period => (
                  <button
                    key={period.value}
                    onClick={() => handleMotorPeriodChange(period.value)}
                    style={{
                      padding: '0.3rem 0.6rem',
                      fontSize: '0.75rem',
                      fontWeight: motorPeriod === period.value ? 600 : 400,
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      background: motorPeriod === period.value ? '#0288d1' : '#f1f5f9',
                      color: motorPeriod === period.value ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease'
                    }}
                    data-testid={`motor-period-${period.value}`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
              <a href="/motor-launches" style={{ fontSize: '0.9rem', color: '#1e88e5', textDecoration: 'none' }}>
                Detaylı Görüntüle →
              </a>
            </div>
          </div>
          <div style={statsGridStyle}>
            <div className="stat-card" data-testid="stat-motor-trips">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">{motorLaunchStats?.period_label || 'Bu Ay'} Seferler</div>
                  <div className="stat-card-value">{motorLaunchStats?.total_trips || 0}</div>
                </div>
                <div className="stat-card-icon green">
                  <Ship size={24} />
                </div>
              </div>
            </div>

            <div className="stat-card" data-testid="stat-motor-levend">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">Levend Seferleri</div>
                  <div className="stat-card-value">{motorLaunchStats?.levend_trips || 0}</div>
                </div>
                <div className="stat-card-icon blue">
                  <Ship size={24} />
                </div>
              </div>
            </div>

            <div className="stat-card" data-testid="stat-motor-rota">
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-title">Rota Seferleri</div>
                  <div className="stat-card-value">{motorLaunchStats?.rota_trips || 0}</div>
                </div>
                <div className="stat-card-icon blue">
                  <Ship size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hızlı İşlemler */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Hızlı İşlemler</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <a href="/proformas" className="btn btn-primary" data-testid="quick-proforma">
                <FileText size={18} />
                Proforma Oluştur
              </a>
              <a href="/invoices" className="btn btn-primary" data-testid="quick-invoice">
                <Receipt size={18} />
                Fatura Oluştur
              </a>
              <a href="/payments" className="btn btn-success" data-testid="quick-payment">
                <DollarSign size={18} />
                Ödeme Kaydet
              </a>
              <a href="/clients" className="btn btn-secondary" data-testid="quick-client">
                <Users size={18} />
                Müşteri Ekle
              </a>
            </div>
          </div>
        </div>

        {/* Exchange Rates Widget */}
        <div style={{ marginTop: '2rem' }}>
          <ExchangeRatesWidget />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
